import './index.css';
import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import BRRApp from './BRRApp';
import { msalInstance, LOGIN_REQUEST } from './auth/msalConfig';
import { getUserRoleFromToken } from './auth/tokenClaims';
import { UserRole } from './services/types';
import type { AccountInfo } from '@azure/msal-browser';
import { readEnv } from './env';
import { getApiToken } from './services/apiToken';

// Sole bootstrap for the Vite-built standalone app (`npm run dev` / `npm run build`).

const API_BASE_URL: string = readEnv('VITE_API_BASE_URL') ?? 'http://localhost:8000';

// The dev-only `?role=` override is host-gated so it can never be used to
// escalate past the token's app role on a deployed host.
function isLocalHost(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

// On localhost a `?role=viewer|supervisor|admin` query param overrides the token
// role so all dashboard tiers can be exercised without signing in as different
// users.
function resolveRole(tokenRole: UserRole): UserRole {
    if (!isLocalHost()) return tokenRole;
    const override = new URLSearchParams(window.location.search).get('role') as UserRole | null;
    return override === 'viewer' || override === 'supervisor' || override === 'admin'
        ? override
        : tokenRole;
}

function showLoading(root: HTMLElement): void {
    root.innerHTML =
        '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;color:#666;">Signing in…</div>';
}

function showError(root: HTMLElement, message: string): void {
    root.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;color:#c00;">${message}</div>`;
}

function showSignInButton(root: HTMLElement): void {
    root.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;gap:16px;">
            <p style="color:#333;margin:0;">Please sign in to continue.</p>
            <button id="brr-signin-btn" style="padding:10px 24px;background:#0067b8;color:#fff;border:none;border-radius:4px;font-size:14px;cursor:pointer;">
                Sign in with Microsoft
            </button>
        </div>
    `;
    const btn = document.getElementById('brr-signin-btn') as HTMLButtonElement;
    btn.addEventListener('click', () => {
        btn.disabled = true;
        btn.textContent = 'Signing in…';
        msalInstance.loginRedirect(LOGIN_REQUEST).catch(console.error);
    });
}

// Checked before sign-in so a missing token surfaces as one clear message here
// rather than as unexplained 401s on every backend call later.
function assertApiTokenConfigured(): void {
    getApiToken();
}

async function bootstrap(): Promise<void> {
    const root = document.getElementById('root')!;
    showLoading(root);

    try {
        assertApiTokenConfigured();
    } catch {
        showError(root, 'Configuration error: VITE_API_TOKEN is not set.');
        return;
    }

    await msalInstance.initialize();
    await msalInstance.handleRedirectPromise();

    let account: AccountInfo | null =
        msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;
    if (account) {
        msalInstance.setActiveAccount(account);
    }

    if (!account) {
        try {
            const result = await msalInstance.ssoSilent(LOGIN_REQUEST);
            account = result.account;
            msalInstance.setActiveAccount(account);
        } catch {
            // Silent sign-in failed — show the sign-in button. After the redirect,
            // handleRedirectPromise() above will pick up the response on next load.
            showSignInButton(root);
            return;
        }
    }

    const userEmail = account.username;
    const userRole = resolveRole(getUserRoleFromToken(account));

    ReactDOM.render(
        React.createElement(
            BrowserRouter,
            null,
            React.createElement(BRRApp, {
                apiBaseUrl: API_BASE_URL,
                userEmail,
                userRole,
            })
        ),
        root
    );
}

bootstrap().catch((err) => {
    console.error('[BRR] Bootstrap failed:', err);
    const root = document.getElementById('root');
    if (root) showError(root, 'Sign-in failed. Please refresh the page.');
});
